import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { voicemailService } from "../services/voicemail-service";
import { toast } from "react-toastify";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const VoicemailReports = () => {
  const [dataType, setDataType] = useState("all");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [categoryReport, setCategoryReport] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [twilioNumber, setTwilioNumber] = useState("");

  const selectRef = useRef(null);
  const datePickerRef = useRef(null);

  // Helper: format date
  const formatDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  // Default month range
  const getDefaultMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(firstDay), end: formatDate(now) };
  };

  // Fetch all reports
  const fetchReports = async (startDate = null, endDate = null) => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (twilioNumber) params.twilio_number = twilioNumber;

      // Fetch all reports in parallel
      const [metricsResponse, categoryResponse, dailyResponse] = await Promise.all([
        voicemailService.fetchVoicemailMetrics(params),
        voicemailService.fetchVoicemailCategoryReport(params),
        voicemailService.fetchVoicemailDailyReport(params),
      ]);

      // Handle different response structures (data.data, data.original, or direct data)
      const metricsData = metricsResponse?.data || metricsResponse?.original || metricsResponse;
      const categoryData = categoryResponse?.data || categoryResponse?.original || categoryResponse;
      const dailyData = dailyResponse?.data || dailyResponse?.original || dailyResponse;

      setMetrics(metricsData);
      setCategoryReport(categoryData);
      setDailyReport(dailyData);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load voicemail reports");
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    let startDate = null;
    let endDate = null;

    if (dataType === "custom" && selectedRange) {
      startDate = selectedRange.start;
      endDate = selectedRange.end;
    } else if (dataType === "monthly") {
      const range = getDefaultMonthRange();
      startDate = range.start;
      endDate = range.end;
    } else if (dataType === "all") {
      // Default to current year
      const now = new Date();
      startDate = `${now.getFullYear()}-01-01`;
      endDate = formatDate(now);
    }

    fetchReports(startDate, endDate);
  }, [dataType, selectedRange, twilioNumber]);

  // Dropdown arrow logic
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);

    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);

    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Close Date Picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  const handleCustomDateClick = () => {
    setDataType("custom");
    setShowDatePicker(true);
  };

  const handleApply = () => {
    if (!tempStartDate || !tempEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    setSelectedRange({ start: tempStartDate, end: tempEndDate });
    setShowDatePicker(false);
  };

  const handleCancel = () => {
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
    setDataType("all");
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${minutes}:${String(secs).padStart(2, "0")}`;
  };

  // Chart colors
  const colors = [
    "#ec3310",
    "#106271",
    "#64868d",
    "#de735e",
    "#00323b",
    "#ffb0a2",
    "#fcd5c9",
    "#d9eef0",
    "#2db3c0",
    "#e29878",
    "#adcfd4",
  ];

  // Category chart data - transform array to chart format
  const categoryChartData = categoryReport && Array.isArray(categoryReport) && categoryReport.length > 0
    ? {
        labels: categoryReport.map(item => item.category || 'Uncategorized'),
        datasets: [
          {
            label: "Voicemails by Category",
            data: categoryReport.map(item => item.count || 0),
            backgroundColor: colors.slice(0, categoryReport.length),
            borderColor: colors.slice(0, categoryReport.length),
            borderWidth: 1,
          },
        ],
      }
    : null;

  // Daily chart data - transform array to chart format
  const dailyChartData = dailyReport && Array.isArray(dailyReport) && dailyReport.length > 0
    ? {
        labels: dailyReport.map(item => {
          const d = new Date(item.date);
          return `${d.getMonth() + 1}/${d.getDate()}`;
        }),
        datasets: [
          {
            label: "Voicemails per Day",
            data: dailyReport.map(item => item.count || 0),
            backgroundColor: "#2db3c0",
            borderColor: "#106271",
            borderWidth: 1,
          },
        ],
      }
    : null;

  return (
    <Wrapper>
      <Header>
        <PageTitle>Voicemail Reports</PageTitle>
        <Controls>
          <SelectContainer>
            <Select
              ref={selectRef}
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
            >
              <option value="all">All Data</option>
              <option value="monthly">Current Month</option>
              <option value="custom">Custom Date Range</option>
            </Select>
            <ChevronDown
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: isSelectOpen
                  ? "translateY(-50%) rotate(180deg)"
                  : "translateY(-50%)",
                pointerEvents: "none",
                transition: "transform 0.2s",
              }}
            />
          </SelectContainer>

          {dataType === "custom" && (
            <DatePickerContainer ref={datePickerRef}>
              <DateInput
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                placeholder="Start Date"
              />
              <DateInput
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                placeholder="End Date"
              />
              <Button onClick={handleApply}>Apply</Button>
              <Button onClick={handleCancel} style={{ background: "#ec3310" }}>
                Cancel
              </Button>
            </DatePickerContainer>
          )}

          <Input
            type="text"
            placeholder="Twilio Number (optional)"
            value={twilioNumber}
            onChange={(e) => setTwilioNumber(e.target.value)}
            style={{ marginLeft: "10px" }}
          />
        </Controls>
      </Header>

      {loading ? (
        <LoadingContainer>
          <LoadingText>Loading reports...</LoadingText>
        </LoadingContainer>
      ) : (
        <>
          {/* Metrics Overview */}
          {metrics && metrics.summary && (
            <MetricsSection>
              <SectionTitle>Overall Metrics</SectionTitle>
              <MetricsGrid>
                <MetricCard>
                  <MetricLabel>Total Voicemails</MetricLabel>
                  <MetricValue>{metrics.summary.total_voicemails || 0}</MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Total Duration</MetricLabel>
                  <MetricValue>
                    {formatDuration(parseInt(metrics.summary.total_duration_seconds) || 0)}
                  </MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Average Duration</MetricLabel>
                  <MetricValue>
                    {formatDuration(parseInt(metrics.summary.average_duration_seconds) || 0)}
                  </MetricValue>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Unique Callers</MetricLabel>
                  <MetricValue>{metrics.summary.unique_callers || 0}</MetricValue>
                </MetricCard>
              </MetricsGrid>
            </MetricsSection>
          )}

          {/* Charts Section */}
          <ChartsSection>
            {/* Category Breakdown */}
            {categoryChartData && (
              <ChartContainer>
                <ChartTitle>Voicemails by Category</ChartTitle>
                <Doughnut data={categoryChartData} />
              </ChartContainer>
            )}

            {/* Daily Breakdown */}
            {dailyChartData && (
              <ChartContainer style={{ width: "100%" }}>
                <ChartTitle>Daily Voicemail Count</ChartTitle>
                <Bar
                  data={dailyChartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              </ChartContainer>
            )}
          </ChartsSection>
        </>
      )}
    </Wrapper>
  );
};

// Styled Components
const Wrapper = styled.div`
  padding: 1rem;
  background: transparent;
  min-height: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  color: #00323b;
  font-family: "Space Grotesk", sans-serif;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SelectContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Select = styled.select`
  padding: 0.5rem 2.5rem 0.5rem 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  font-size: 1rem;
  cursor: pointer;
  appearance: none;
  min-width: 180px;
`;

const DatePickerContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #ccc;
`;

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 200px;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: #2db3c0;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover {
    opacity: 0.9;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: #00323b;
`;

const MetricsSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #00323b;
  margin-bottom: 1rem;
  font-family: "Space Grotesk", sans-serif;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const MetricCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const MetricLabel = styled.div`
  font-size: 0.9rem;
  color: #64868d;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  color: #00323b;
  font-weight: bold;
`;

const ChartsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ChartContainer = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 300px;
`;

const ChartTitle = styled.h3`
  font-size: 1.2rem;
  color: #00323b;
  margin-bottom: 1rem;
  font-family: "Space Grotesk", sans-serif;
  text-align: center;
`;

export default VoicemailReports;

